import React, { Dispatch, useCallback, useEffect, useRef } from 'react';
import { AuthProvider, useAuth, useFirebaseApp } from 'reactfire';
import { i18n } from 'next-i18next';

import {
  initializeAuth,
  indexedDBLocalPersistence,
  connectAuthEmulator,
  User,
} from 'firebase/auth';

import { isBrowser } from '~/core/generic/is-browser';
import { resolveEmulatorTarget } from '~/core/firebase/emulator-runtime-config';
import useClearFirestoreCache from '~/core/hooks/use-clear-firestore-cache';
import { useDestroySession } from '~/core/hooks/use-destroy-session';
import { consumeSessionRecoveryClientSignOutSuppression } from '~/core/session/client-session-recovery';
import { UserSession } from '~/core/session/types/user-session';

const DEBUG_E2E =
  process.env.NEXT_PUBLIC_IS_CI === 'true' ||
  process.env.NODE_ENV !== 'production';

export const FirebaseAuthStateListener: React.FCC<{
  onAuthStateChange: (user: User | null) => void | Promise<void>;
}> = ({ children, onAuthStateChange }) => {
  const auth = useAuth();
  const onAuthStateChangeRef = useRef(onAuthStateChange);

  onAuthStateChangeRef.current = onAuthStateChange;

  // {@link onIdTokenChanged} will call the
  // callback when the user ID token changes
  // for example, when the user signs out
  // we update user context when ID token changes
  useEffect(() => {
    const subscription = auth.onIdTokenChanged((user) =>
      onAuthStateChangeRef.current(user),
    );

    return () => subscription();
  }, [auth]);

  return <>{children}</>;
};

export default function FirebaseAuthProvider({
  userSession,
  setUserSession,
  children,
  useEmulator,
}: React.PropsWithChildren<{
  useEmulator: boolean;
  userSession: Maybe<UserSession>;
  setUserSession: Dispatch<Maybe<UserSession>>;
}>) {
  const app = useFirebaseApp();
  const { trigger: signOut } = useDestroySession();
  const clearFirestoreCache = useClearFirestoreCache();
  const hasAuthenticatedSessionRef = useRef(!!userSession?.auth);

  // make sure we're not using IndexedDB when SSR
  // as it is only supported on browser environments
  const persistence = isBrowser() ? indexedDBLocalPersistence : undefined;

  const sdk = initializeAuth(app, { persistence });
  const shouldConnectEmulator = useEmulator && !('emulator' in sdk.config);

  if (DEBUG_E2E && typeof window !== 'undefined') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).__ddAuthConfig = sdk.config;
  }

  // set the language code for the SDK
  if (i18n?.language) {
    sdk.languageCode = i18n?.language;
  }

  if (shouldConnectEmulator) {
    const host = getAuthEmulatorHost();

    connectAuthEmulator(sdk, host);

    if (DEBUG_E2E && typeof window !== 'undefined') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__ddAuthEmulatorHost = host;
    }
  }

  const onAuthStateChanged = useCallback(
    async (user: User | null) => {
      if (user) {
        const session = {
          auth: user,
          data: userSession?.data,
        };

        hasAuthenticatedSessionRef.current = true;

        return setUserSession(session);
      }

      // if the user is no longer defined and user was originally signed-in
      // (because userSession?.auth is defined) then we need to clear the
      // session cookie
      const hadAuthenticatedSession = hasAuthenticatedSessionRef.current;

      // Clear the transition synchronously before any state update or request.
      // Firebase may replay the current null state, and duplicate null events
      // must not consume recovery suppression or destroy the server session
      // more than once.
      hasAuthenticatedSessionRef.current = false;

      const isRecoveryClientSignOut =
        consumeSessionRecoveryClientSignOutSuppression();

      if (isRecoveryClientSignOut) {
        clearFirestoreCache();
        setUserSession(undefined);

        return;
      }

      if (!hadAuthenticatedSession) {
        return;
      }

      try {
        // we need to delete the session cookie used for SSR
        await signOut();
      } finally {
        clearFirestoreCache();
        setUserSession(undefined);
      }
    },
    [clearFirestoreCache, setUserSession, signOut, userSession?.data],
  );

  return (
    <AuthProvider sdk={sdk}>
      <FirebaseAuthStateListener onAuthStateChange={onAuthStateChanged}>
        {children}
      </FirebaseAuthStateListener>
    </AuthProvider>
  );
}

function getAuthEmulatorHost() {
  const { host, port } = resolveEmulatorTarget('auth');

  return `http://${host}:${port}`;
}
